/**
 * @fileoverview Domain types shared across the application.
 * @module types/types
 */

/**
 * The two personalities the app can wear.
 *
 * @description
 * Declared as a const object rather than a TS `enum` because the project
 * compiles with `erasableSyntaxOnly`: nothing that emits runtime code.
 */
export const AppMode = {
	/** The mountain: ascension, patience, the summit. */
	PEAK: "peak",
	/** The iron: overload, volume, noise. */
	TESTO: "testo",
} as const

/** Union of every supported mode. */
export type AppMode = (typeof AppMode)[keyof typeof AppMode]

/** Unique identifier of a phrase in the catalogue. */
export type PhraseId = string

/** One motivational line, belonging to exactly one mode. */
export interface Phrase {
	/** Unique identifier, stable across reloads so a session can be restored. */
	readonly id: PhraseId
	/** The mode this line belongs to. */
	readonly mode: AppMode
	/** The line itself, written as it should be read. */
	readonly text: string
}

/**
 * What the app remembers between reloads.
 *
 * @description
 * The last phrase is kept *per mode*, not globally. That is what lets the
 * mode switch be instant and free while the draw stays locked: flipping to
 * Testo shows the line Testo was already on, so switching can never be used
 * to dodge the cooldown.
 */
export interface Session {
	/** The active mode. */
	readonly mode: AppMode
	/** Last phrase seen in each mode, or null before that mode's first draw. */
	readonly phraseIds: Readonly<Record<AppMode, PhraseId | null>>
	/** ISO-8601 instant of the last draw, or null when none happened yet. */
	readonly drawnAt: string | null
}

/** Where the draw cooldown stands at a given instant. */
export interface Cooldown {
	/** True when a new phrase can be drawn. */
	readonly isReady: boolean
	/** Milliseconds left before the next draw. Zero once ready. */
	readonly remainingMs: number
	/** Share of the cooldown already elapsed, 0 to 1. */
	readonly ratio: number
}

/** How the synthesiser should shout a phrase. */
export interface VoiceSettings {
	/** BCP-47 language tag the voice is picked from. */
	readonly lang: string
	/** Speaking rate, 0.1 to 10 — 1 is the engine's normal pace. */
	readonly rate: number
	/** Pitch, 0 to 2 — below 1 sounds heavier. */
	readonly pitch: number
	/** Volume, 0 to 1. This app has exactly one setting for it. */
	readonly volume: number
}
